import { useAlert } from '@components/alerts';
import { TextField } from '@components/textField';
import { updateUser } from '@services/api';
import { isCompleteName } from '@services/personName';
import { useMe, useSaving } from '@services/state';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../layout/public';

export interface CompleteYourNameProps {
  /** What happens once the name is saved. Defaults to re-reading the record. */
  onDone?: () => void;
  /** Saves the name somewhere other than the user record, for the invitation path. */
  onSubmitName?: (name: { givenName: string; familyName: string }) => Promise<void>;
  title?: string;
  message?: string;
}

/**
 * Asks somebody for their name, once, and will not continue without it.
 *
 * There is no cancel and no dismiss, deliberately. A record with no name is why a proposal printed
 * an account handle where a person belonged, why a project manager showed as an email address, and
 * why an avatar once threw on an empty string - each patched where it surfaced. This is the one
 * place the cause can be fixed for a record that already exists, because the only honest way to
 * learn somebody's name is to ask them.
 *
 * It does not invent anything and it does not guess from an email address. If somebody will not
 * give a name they stay where they are; signing out is always available from the topbar.
 */
export const CompleteYourName: React.FC<CompleteYourNameProps> = ({ onDone, onSubmitName, title, message }) => {
  const { t } = useTranslation();
  const [me, refreshMe] = useMe();
  const [saving, setSaving] = useSaving();
  const { alertDanger } = useAlert();

  const [givenName, setGivenName] = useState(me?.givenName ?? '');
  const [familyName, setFamilyName] = useState(me?.familyName ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const submit = useCallback(async () => {
    const next: Record<string, string> = {};
    if (!givenName.trim()) next.givenName = t('General.Errors.Required', 'Required');
    if (!familyName.trim()) next.familyName = t('General.Errors.Required', 'Required');
    setErrors(next);
    if (Object.keys(next).length) return;

    const name = { givenName: givenName.trim(), familyName: familyName.trim() };

    setSaving(true);
    try {
      if (onSubmitName) {
        await onSubmitName(name);
      } else {
        if (!me) return;
        await updateUser(null, me.id, { ...me, ...name });
        await refreshMe();
      }
      onDone?.();
    } catch (err) {
      alertDanger({
        title: t('Name.Errors.SaveTitle', 'Your name could not be saved'),
        message: t('Name.Errors.Save', 'Nothing was changed. Try again.'),
        error: err,
      });
    } finally {
      setSaving(false);
    }
  }, [givenName, familyName, me, onDone, onSubmitName, refreshMe, setSaving, alertDanger, t]);

  return (
    <Layout className="invite">
      <div className="ui-text-center ui-text-lg" style={{ marginBottom: 8 }}>
        {title ?? t('Name.Title', 'What should we call you?')}
      </div>
      <div className="error-text ui-text-center" style={{ marginBottom: 20 }}>
        {message ??
          t(
            'Name.Message',
            'Your name goes on the projects you work on and the documents they produce, so we need it before you carry on.',
          )}
      </div>

      <div className="ui-row">
        <div className="ui-col-6">
          <TextField
            name="givenName"
            type="text"
            value={givenName}
            error={errors.givenName}
            label={t('Name.GivenName', 'First name')}
            onChange={(v) => setGivenName(v)}
          />
        </div>
        <div className="ui-col-6">
          <TextField
            name="familyName"
            type="text"
            value={familyName}
            error={errors.familyName}
            label={t('Name.FamilyName', 'Last name')}
            onChange={(v) => setFamilyName(v)}
          />
        </div>
      </div>

      <div className="ui-text-center" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="ui-btn ui-btn-primary ui-btn-solid"
          disabled={saving || !isCompleteName({ givenName, familyName })}
          onClick={() => void submit()}
        >
          {saving ? t('General.Saving', 'Saving') : t('General.Continue', 'Continue')}
        </button>
      </div>
    </Layout>
  );
};
